import React, { useState, useEffect } from 'react';
import { projects } from '../data/portfolioData';

const Projects = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  useEffect(() => {
    if (!isAutoPlay) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % projects.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [isAutoPlay]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % projects.length);
    setIsAutoPlay(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + projects.length) % projects.length);
    setIsAutoPlay(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlay(false);
  };

  return (
    <section id="projects">
      <div className="section-header animate-fade-in-up">
        <span className="section-number">05.</span>
        <h2 className="section-title">Projects</h2>
        <div className="section-line"></div>
      </div>

      {/* Slideshow Section */}
      <div className="slideshow-container">
        <div 
          className="slideshow-wrapper"
          onMouseEnter={() => setIsAutoPlay(false)}
          onMouseLeave={() => setIsAutoPlay(true)}
        >
          <div className="slides-track">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`slide ${index === currentSlide ? 'active' : ''}`}
              >
                <div className="slide-content">
                  <p className="slide-label">Featured Project</p>
                  <h3 className="slide-title">{project.title}</h3>
                  <p className="slide-description">{project.description}</p>
                  
                  <ul className="slide-achievements">
                    {project.achievements.map((achievement, i) => (
                      <li key={i}>
                        <span className="slide-badge">→</span>
                        {achievement}
                      </li>
                    ))}
                  </ul>

                  <div className="slide-tech">
                    {project.tech.map((tech, i) => (
                      <span key={i} className="slide-tech-item">{tech}</span>
                    ))}
                  </div>

                  <div className="slide-cta">
                    {project.githubUrl && project.githubUrl[0] && (
                      <a 
                        href={project.githubUrl[0]} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="slide-btn"
                      >
                        View on GitHub
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button 
            className="slide-nav prev-btn"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <span>❮</span>
          </button>
          <button 
            className="slide-nav next-btn"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <span>❯</span>
          </button>

          {/* Slide Indicators */}
          <div className="slide-indicators">
            {projects.map((_, index) => (
              <button
                key={index}
                className={`indicator-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Autoplay Toggle */}
          <button 
            className="autoplay-toggle"
            onClick={() => setIsAutoPlay(!isAutoPlay)}
            title={isAutoPlay ? 'Pause autoplay' : 'Resume autoplay'}
          >
            <span>{isAutoPlay ? '⏸' : '▶'}</span>
          </button>
        </div>
      </div>

      {/* Grid Fallback */}
      <div className="projects-grid">
        {projects.map((project, index) => (
          <div 
            key={index} 
            className={`project-card animate-scale-in ${index % 3 === 0 ? 'delay-1 slide-from-left' : index % 3 === 1 ? 'delay-2 slide-from-bottom' : 'delay-3 slide-from-right'}`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {/* Animated Background */}
            <div className="project-card-bg"></div>
            <div className="project-card-border"></div>
            
            <div className="project-content">
              <p className="project-label animate-fade-in-down">Featured Project</p>
              <h3 className="project-title animate-slide-in-right">{project.title}</h3>
              
              <div className="project-description animate-slide-in-up">
                <p className="description-text">{project.description}</p>
                <ul className="achievements">
                  {project.achievements.map((achievement, i) => (
                    <li key={i} className={`achievement-item ${hoveredIndex === index ? 'active' : ''}`}>
                      <span className="achievement-badge">✓</span>
                      <span className="achievement-text">{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="project-tech-wrapper">
                <div className="project-tech">
                  {project.tech.map((tech, i) => (
                    <span 
                      key={i} 
                      className="tech-badge animate-slide-in-up"
                      style={{ animationDelay: `${0.5 + i * 0.1}s` }}
                    >
                      <span className="tech-dot"></span>
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="project-links">
                {project.githubUrl && project.githubUrl.length > 0 && project.githubUrl[0] && (
                  <a 
                    href={project.githubUrl[0]} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="github-link-btn animate-slide-in-up"
                  >
                    <span className="link-icon">→</span>
                    <span className="link-text">GitHub</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        /* ========== SLIDESHOW STYLES ========== */
        .slideshow-container {
          margin-bottom: 4rem;
          animation: slideInUp 0.8s ease-out;
          display: block;
        }

        @media (max-width: 768px) {
          .slideshow-container {
            display: none;
          }
        }

        .slideshow-wrapper {
          position: relative;
          width: 100%;
          max-width: 1000px;
          margin: 0 auto;
          background: rgba(255, 255, 255, 0.03);
          border: 2px solid rgba(0, 255, 247, 0.2);
          border-radius: 16px;
          overflow: hidden;
          backdrop-filter: blur(10px);
          box-shadow: 0 8px 32px rgba(0, 255, 247, 0.1);
        }

        .slides-track {
          position: relative;
          width: 1000px;
          height: 500px;
          overflow: hidden;
          padding: 0 50px;
          box-sizing: border-box;
        }

        @media (max-width: 1024px) {
          .slides-track {
            height: 450px;
            padding: 0 60px;
          }
        }

        .slide {
          position: absolute;
          width: 900px;
          height: 100%;
          padding: 1rem 1rem;
          opacity: 0;
          transform: translateX(100%);
          transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
          z-index: 0;
        }

        .slide.active {
          opacity: 1;
          transform: translateX(0);
          z-index: 10;
          animation: slideInLeft 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .slide-content {
          display: flex;
          flex-direction: column;
          height: 100%;
          justify-content: space-between;
          width: 100%;

        }

        .slide-label {
          font-size: 0.85rem;
          color: #00fff7;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 600;
          margin: 0 0 1rem 0;
          animation: fadeInDown 0.6s ease-out 0.2s both;
        }

        .slide-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 1rem 0;
          background: linear-gradient(135deg, #ffffff 0%, #00fff7 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: slideInRight 0.7s ease-out 0.3s both;
        }

        .slide-description {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.7;
          margin: 0 0 1.5rem 0;
          animation: fadeInUp 0.6s ease-out 0.4s both;
        }

        .slide-achievements {
          list-style: none;
          padding: 0;
          margin: 0 0 1.5rem 0;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .slide-achievements li {
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          gap: 0.75rem;
          animation: slideContentRight 0.6s ease-out both;
        }

        .slide-achievements li:nth-child(1) { animation-delay: 0.5s; }
        .slide-achievements li:nth-child(2) { animation-delay: 0.6s; }
        .slide-achievements li:nth-child(3) { animation-delay: 0.7s; }
        .slide-achievements li:nth-child(4) { animation-delay: 0.8s; }

        .slide-badge {
          color: #00fff7;
          font-weight: bold;
          display: inline-block;
          animation: rotateBadge 2s linear infinite;
        }

        .slide-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .slide-tech-item {
          display: inline-block;
          padding: 0.5rem 1rem;
          background: rgba(0, 255, 247, 0.1);
          border: 1px solid rgba(0, 255, 247, 0.3);
          border-radius: 20px;
          color: #00fff7;
          font-size: 0.85rem;
          font-weight: 500;
          transition: all 0.3s ease;
          animation: slideBadgeLeft 0.5s ease-out both;
        }

        .slide-tech-item:nth-child(1) { animation-delay: 0.5s; }
        .slide-tech-item:nth-child(2) { animation-delay: 0.6s; }
        .slide-tech-item:nth-child(3) { animation-delay: 0.7s; }
        .slide-tech-item:nth-child(4) { animation-delay: 0.8s; }
        .slide-tech-item:nth-child(5) { animation-delay: 0.9s; }

        .slide-tech-item:hover {
          background: rgba(0, 255, 247, 0.2);
          border-color: rgba(0, 255, 247, 0.6);
          box-shadow: 0 0 15px rgba(0, 255, 247, 0.3);
          transform: translateY(-3px);
        }

        .slide-cta {
          display: flex;
          animation: fadeInUp 0.6s ease-out 0.9s both;
        }

        .slide-btn {
          padding: 0.85rem 2rem;
          background: linear-gradient(135deg, rgba(0, 255, 247, 0.15), rgba(0, 215, 255, 0.1));
          border: 2px solid #00fff7;
          border-radius: 8px;
          color: #00fff7;
          font-size: 0.85rem;
          font-weight: 300;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .slide-btn::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(0, 255, 247, 0.25);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
          z-index: -1;
        }

        .slide-btn:hover {
          box-shadow: 0 0 25px rgba(0, 255, 247, 0.5);
          transform: translateY(-3px);
        }

        .slide-btn:hover::before {
          width: 300px;
          height: 300px;
        }

        /* Navigation Arrows */
        .slide-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 255, 247, 0.1);
          border: 2px solid rgba(0, 255, 247, 0.3);
          color: #00fff7;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          font-size: 1.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 20;
        }

        .prev-btn {
          left: 1rem;
        }

        .next-btn {
          right: 1rem;
        }

        @media (max-width: 1024px) {
          .slide-nav {
            width: 45px;
            height: 45px;
            font-size: 1.2rem;
          }

          .prev-btn {
            left: 0.75rem;
          }

          .next-btn {
            right: 0.75rem;
          }
        }

        @media (max-width: 768px) {
          .slide-nav {
            display: none;
          }
        }

        .slide-nav:hover {
          background: rgba(0, 255, 247, 0.25);
          border-color: #00fff7;
          box-shadow: 0 0 20px rgba(0, 255, 247, 0.4);
          transform: translateY(-50%) scale(1.1);
        }

        .slide-nav:active {
          transform: translateY(-50%) scale(0.95);
        }

        /* Slide Indicators */
        .slide-indicators {
          display: flex;
          justify-content: center;
          gap: 0.75rem;
          padding: 1.5rem;
          background: rgba(0, 0, 0, 0.2);
        }

        .indicator-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid rgba(0, 255, 247, 0.3);
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .indicator-dot:hover {
          border-color: #00fff7;
          transform: scale(1.3);
        }

        .indicator-dot.active {
          background: #00fff7;
          border-color: #00fff7;
          box-shadow: 0 0 15px rgba(0, 255, 247, 0.6);
          animation: pulse 1.5s ease-in-out infinite;
        }

        /* Autoplay Toggle */
        .autoplay-toggle {
          position: absolute;
          bottom: 1.5rem;
          right: 1.5rem;
          background: rgba(0, 255, 247, 0.1);
          border: 2px solid rgba(0, 255, 247, 0.3);
          color: #00fff7;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 20;
        }

        .autoplay-toggle:hover {
          background: rgba(0, 255, 247, 0.2);
          border-color: #00fff7;
          box-shadow: 0 0 15px rgba(0, 255, 247, 0.4);
          transform: scale(1.1);
        }

        @media (max-width: 768px) {
          .autoplay-toggle {
            display: none;
          }
        }

        /* ========== GRID STYLES ========== */
        .projects-grid {
          display: none;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          perspective: 1000px;
        }

        @media (max-width: 768px) {
          .projects-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }

        /* ============ SLIDING ANIMATIONS ============ */
        @keyframes slideFromLeft {
          from {
            opacity: 0;
            transform: translateX(-80px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes slideFromRight {
          from {
            opacity: 0;
            transform: translateX(80px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes slideFromBottom {
          from {
            opacity: 0;
            transform: translateY(60px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideContentUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideContentRight {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideBadgeLeft {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* ============ SLIDING ANIMATIONS ============ */

        /* Apply slide animations */
        .slide-from-left {
          animation: slideFromLeft 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }

        .slide-from-right {
          animation: slideFromRight 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }

        .slide-from-bottom {
          animation: slideFromBottom 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }

        .animate-slide-in-right {
          animation: slideContentRight 0.7s ease-out 0.3s both !important;
        }

        .animate-slide-in-up {
          animation: slideContentUp 0.7s ease-out both !important;
        }

        .project-card {
          position: relative;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(0, 255, 247, 0.1);
          border-radius: 12px;
          padding: 2rem;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          cursor: pointer;
          min-height: 350px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .project-card:hover {
          transform: translateY(-15px) scale(1.02);
          border-color: rgba(0, 255, 247, 0.4);
          background: rgba(255, 255, 255, 0.04);
          box-shadow: 0 20px 40px rgba(0, 255, 247, 0.2), 0 0 80px rgba(0, 215, 255, 0.1);
        }

        .project-card-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(0, 255, 247, 0.05) 0%, rgba(0, 215, 255, 0) 100%);
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: 0;
        }

        .project-card:hover .project-card-bg {
          opacity: 1;
        }

        .project-card-border {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #00fff7, transparent);
          animation: slideRightBorder 2s ease-in-out infinite;
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .project-card:hover .project-card-border {
          opacity: 1;
        }

        @keyframes slideRightBorder {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(-100%);
          }
        }

        .project-content {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .project-label {
          font-size: 0.85rem;
          color: #00fff7;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 600;
          margin: 0;
          opacity: 0.8;
          animation: pulse 2s ease-in-out infinite;
        }

        .project-title {
          font-size: 1.6rem;
          color: #ffffff;
          margin: 0;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 0%, #00fff7 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          transition: text-shadow 0.3s ease;
        }

        .project-card:hover .project-title {
          text-shadow: 0 0 20px rgba(0, 255, 247, 0.5);
        }

        .project-description {
          flex-grow: 1;
        }

        .description-text {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
          margin: 0 0 1rem 0;
          font-size: 0.95rem;
          animation: slideContentUp 0.8s ease-out 0.4s both;
        }

        .achievements {
          list-style: none;
          padding: 0;
          margin: 0.75rem 0 0 0;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .achievement-item {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.3s ease;
          animation: fadeInSlideRight 0.6s ease-out both;
          position: relative;
        }

        .achievement-item:nth-child(1) { animation-delay: 0.7s; }
        .achievement-item:nth-child(2) { animation-delay: 0.8s; }
        .achievement-item:nth-child(3) { animation-delay: 0.9s; }
        .achievement-item:nth-child(4) { animation-delay: 1s; }

        .project-card:hover .achievement-item {
          color: rgba(255, 255, 255, 0.95);
          transform: translateX(8px);
          animation: slideBadgeLeft 0.4s ease-out forwards;
        }

        .achievement-item:nth-child(1):hover { animation-delay: 0s; }
        .achievement-item:nth-child(2):hover { animation-delay: 0.1s; }
        .achievement-item:nth-child(3):hover { animation-delay: 0.2s; }
        .achievement-item:nth-child(4):hover { animation-delay: 0.3s; }

        .achievement-text {
          display: inline-block;
          animation: slideContentRight 0.5s ease-out 0.1s both;
        }

        .achievement-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: rgba(0, 255, 247, 0.1);
          border: 1px solid #00fff7;
          border-radius: 50%;
          color: #00fff7;
          font-size: 0.75rem;
          font-weight: bold;
          flex-shrink: 0;
          animation: rotateBadge 2s linear infinite;
        }

        .project-card:hover .achievement-badge {
          background: rgba(0, 255, 247, 0.2);
          box-shadow: 0 0 10px rgba(0, 255, 247, 0.4);
        }

        @keyframes rotateBadge {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.1); }
          100% { transform: rotate(360deg) scale(1); }
        }

        @keyframes fadeInSlideRight {
          from {
            opacity: 0;
            transform: translateX(-15px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .project-tech-wrapper {
          overflow: hidden;
        }

        .project-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 0.5rem;
          animation: slideContentUp 0.8s ease-out 0.5s both;
        }

        .tech-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 1rem;
          background: rgba(0, 255, 247, 0.08);
          border: 1px solid rgba(0, 255, 247, 0.2);
          border-radius: 20px;
          color: #00fff7;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          animation: slideBadgeLeft 0.5s ease-out both;
        }

        .tech-badge::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(0, 255, 247, 0.3), transparent);
          transition: left 0.6s ease;
        }

        .tech-badge:hover {
          background: rgba(0, 255, 247, 0.15);
          border-color: rgba(0, 255, 247, 0.5);
          box-shadow: 0 0 15px rgba(0, 255, 247, 0.3);
          transform: translateY(-3px);
        }

        .tech-badge:hover::before {
          left: 100%;
        }

        .tech-dot {
          display: inline-block;
          width: 5px;
          height: 5px;
          background: #00fff7;
          border-radius: 50%;
          animation: dotPulse 1.5s ease-in-out infinite;
        }

        @keyframes dotPulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(1.3);
          }
        }

        .project-links {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
          animation: slideContentUp 0.8s ease-out 0.6s both;
        }

        .github-link-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, rgba(0, 255, 247, 0.1), rgba(0, 215, 255, 0.1));
          border: 1.5px solid #00fff7;
          border-radius: 6px;
          color: #00fff7;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          cursor: pointer;
        }

        .github-link-btn::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(0, 255, 247, 0.2);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }

        .github-link-btn:hover {
          background: linear-gradient(135deg, rgba(0, 255, 247, 0.2), rgba(0, 215, 255, 0.2));
          box-shadow: 0 0 20px rgba(0, 255, 247, 0.4), inset 0 0 20px rgba(0, 255, 247, 0.1);
          transform: scale(1.05) translateX(2px);
        }

        .github-link-btn:hover::before {
          width: 300px;
          height: 300px;
        }

        .link-icon {
          display: inline-block;
          transition: transform 0.3s ease;
          font-weight: bold;
          animation: slideContentRight 0.6s ease-out 0.1s both;
        }

        .github-link-btn:hover .link-icon {
          transform: translateX(6px);
        }

        .link-text {
          position: relative;
          z-index: 1;
          animation: slideContentRight 0.6s ease-out 0.15s both;
        }

        @media (max-width: 768px) {
          .project-card {
            min-height: 300px;
            padding: 1.5rem;
          }

          .project-title {
            font-size: 1.3rem;
          }

          .project-card:hover {
            transform: translateY(-10px);
          }

          .project-description p {
            font-size: 0.9rem;
          }

          .achievements {
            gap: 0.5rem;
          }

          .achievement-item {
            font-size: 0.85rem;
          }
        }
      `}</style>
    </section>
  );
};

export default Projects;